import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import coinIcon from '../assets/icons/coin.png';
import { CREATE_TOO_CLOSE_MESSAGE } from '../services/users/earnCatalog';
import { levelAfterEarn } from '../services/users/userLevels';
import { trackUxEvent } from '../services/uxEvents';
import './CoinToast.css';

const CoinToastContext = createContext({
  notifyCoinEarn: () => {},
  notifyWalkerEarn: () => {},
  notifyEarnStatus: () => {},
  notifyDropCreateResult: () => {},
});

export function useCoinToast() {
  return useContext(CoinToastContext);
}

/** Safe band so chips clear slim top nav and bottom nav. */
function randomToastPosition() {
  const topPct = 12 + Math.random() * 60; // 12–72%
  const leftPct = 8 + Math.random() * 60; // 8–68%
  return { topPct, leftPct };
}

const TOAST_MS = {
  coin: 1400,
  level: 2000,
  status: 2200,
};

/**
 * Global +{N} coin earn toast overlay (explore-like dark chip, random spot).
 * Also hosts level-up and short earn-status chips — same overlay family.
 */
export function CoinToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const pushToast = useCallback((toast, ms) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const { topPct, leftPct } = randomToastPosition();
    setToasts((prev) => [...prev, { id, topPct, leftPct, ...toast }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, ms);
  }, []);

  const notifyCoinEarn = useCallback(
    (amount) => {
      const n = Number(amount) || 0;
      if (n <= 0) return;
      pushToast({ kind: 'coin', amount: n }, TOAST_MS.coin);
    },
    [pushToast]
  );

  const notifyLevelUp = useCallback(
    (rank) => {
      const level = Math.max(1, Math.floor(Number(rank?.level) || 0));
      const title = String(rank?.title || '').trim();
      if (!level || !title) return;
      pushToast({ kind: 'level', level, title }, TOAST_MS.level);
    },
    [pushToast]
  );

  const notifyEarnStatus = useCallback(
    (message) => {
      const text = String(message || '').trim();
      if (!text) return;
      pushToast({ kind: 'status', message: text }, TOAST_MS.status);
    },
    [pushToast]
  );

  const notifyWalkerEarn = useCallback(
    (amount, profile) => {
      const n = Number(amount) || 0;
      if (n <= 0) return;
      const { after, leveledUp } = levelAfterEarn(profile, n);
      notifyCoinEarn(n);
      if (leveledUp) {
        notifyLevelUp(after);
        trackUxEvent({
          name: 'walker_level_up',
          title: after.title,
          source: String(after.level),
        });
      }
    },
    [notifyCoinEarn, notifyLevelUp]
  );

  const notifyDropCreateResult = useCallback(
    (pointsEarned, profile) => {
      const pts = Number(pointsEarned) || 0;
      if (pts > 0) {
        notifyWalkerEarn(pts, profile);
        return;
      }
      notifyEarnStatus(CREATE_TOO_CLOSE_MESSAGE);
    },
    [notifyWalkerEarn, notifyEarnStatus]
  );

  const value = useMemo(
    () => ({
      notifyCoinEarn,
      notifyWalkerEarn,
      notifyEarnStatus,
      notifyDropCreateResult,
    }),
    [notifyCoinEarn, notifyWalkerEarn, notifyEarnStatus, notifyDropCreateResult]
  );

  return (
    <CoinToastContext.Provider value={value}>
      {children}
      <div className="coin-toast-layer" aria-live="polite" aria-atomic="true">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`coin-toast${t.kind === 'level' ? ' coin-toast--level' : ''}${
              t.kind === 'status' ? ' coin-toast--status' : ''
            }`}
            style={{ top: `${t.topPct}%`, left: `${t.leftPct}%` }}
            role="status"
          >
            {t.kind === 'coin' ? (
              <>
                <span className="coin-toast__amount">+{t.amount}</span>
                <img
                  src={coinIcon}
                  alt=""
                  className="coin-toast__icon"
                  width={20}
                  height={20}
                  draggable={false}
                />
              </>
            ) : null}
            {t.kind === 'level' ? (
              <span className="coin-toast__amount">
                Lv {t.level} {t.title}
              </span>
            ) : null}
            {t.kind === 'status' ? (
              <span className="coin-toast__amount">{t.message}</span>
            ) : null}
          </div>
        ))}
      </div>
    </CoinToastContext.Provider>
  );
}
